import { CATALOGO, SETORES } from "@caint/data";
import { useMemo, useState } from "react";
import { NIVEIS_MATURIDADE, escalaCompleta, rotuloNivel } from "../lib/escalaMaturidade.ts";
import "./print.css";

/** Níveis a circular à mão no papel, e a legenda impressa no cabeçalho. Ambos
 *  derivados da escala (antes eram texto solto, e "Incipiente" já havia
 *  divergido do rótulo do motor, "Estado Inicial"). */
const NIVEIS = NIVEIS_MATURIDADE.map(rotuloNivel);
const LEGENDA = escalaCompleta()
  .map((d) => `${d.rotulo} ${d.situacao}`)
  .join(" · ");

/**
 * Monta os grupos a imprimir a partir da seleção ("todos" = unidade inteira, ou
 * um código de setor). Função pura para ser testável sem DOM.
 */
export function agruparParaImpressao(selecao: string) {
  const setores = selecao === "todos" ? SETORES : SETORES.filter((s) => s.code === selecao);
  return setores.map((setor) => ({
    setor,
    itens: CATALOGO.filter((it) => it.sector === setor.code),
  }));
}

export interface ChecklistImpressaoProps {
  /** Volta à tela anterior (mobile). Ausente no desktop (navegação por sidebar). */
  onBack?: () => void;
  /** Setor pré-selecionado (ao vir do Checklist). Sem ele, começa "todos". */
  setorInicial?: string;
  /** Nome do avaliador logado (pré-preenchido no cabeçalho, se houver). */
  avaliador?: string;
}

/**
 * Contingência offline: gera o checklist **em branco** para imprimir (ou salvar
 * em PDF) e preencher à mão quando há dificuldade de acesso ao app. Lê o catálogo
 * embarcado (`@caint/data`), então funciona 100% sem internet.
 */
export function ChecklistImpressao({ onBack, setorInicial, avaliador }: ChecklistImpressaoProps) {
  const [selecao, setSelecao] = useState<string>(setorInicial ?? "todos");
  const grupos = useMemo(() => agruparParaImpressao(selecao), [selecao]);

  return (
    <div className="dk-page">
      <div className="no-print imp-controls">
        {onBack && (
          <button type="button" className="m-btn" onClick={onBack}>
            Voltar
          </button>
        )}
        <label htmlFor="imp-setor">Setor</label>
        <select
          id="imp-setor"
          className="m-select"
          value={selecao}
          onChange={(e) => setSelecao(e.target.value)}
        >
          <option value="todos">Unidade inteira ({SETORES.length} setores)</option>
          {SETORES.map((s) => (
            <option key={s.code} value={s.code}>
              {s.code} — {s.nome}
            </option>
          ))}
        </select>
        <button type="button" className="m-btn primary" onClick={() => window.print()}>
          Imprimir
        </button>
        <span className="imp-hint">
          Checklist em branco para preencher à mão (contingência offline). Imprima ou salve em PDF; depois
          transcreva as notas no app.
        </span>
      </div>

      <div className="print-sheet">
        {grupos.map(({ setor, itens }) => {
          return (
            <section className="print-setor" key={setor.code}>
              <div className="ps-title">
                {setor.code} — {setor.nome}
              </div>
              <div className="ps-meta">
                Unidade: __________________ · Ciclo: __________ · Avaliador: {avaliador ?? "________________"}{" "}
                · Data: ___/___/____
              </div>
              <div className="ps-legend">Escala: {LEGENDA} | Peso: 3 CORE · 2 Importante · 1 Desejável</div>

              <table className="ps-table">
                <thead>
                  <tr>
                    <th className="c-cod">Cód.</th>
                    <th className="c-item">Item de verificação</th>
                    <th className="c-mat">Maturidade</th>
                    <th className="c-evi">Evidência observada</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((it) => (
                    <tr key={it.id}>
                      <td className="c-cod">{it.code}</td>
                      <td className="c-item">
                        <div className="it-pergunta">
                          {it.peso === 3 && <span className="it-core">CORE</span>}
                          {it.pergunta}
                        </div>
                        <div className="it-sub">
                          {it.eixo} · {it.norma} · Evidência sugerida: {it.evidencia}
                        </div>
                      </td>
                      <td className="c-mat">
                        {NIVEIS.map((n) => (
                          <span className="cb" key={n}>
                            {n}
                          </span>
                        ))}
                      </td>
                      <td className="c-evi" />
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="ps-foot">
                Assinatura do avaliador: ____________________________ Supervisor (AUDIN):
                ____________________________
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
